import supabase from "@/services/supabaseClient.js";
import { getCurrentPatientId } from "@/services/appointmentService.js";
import { paymentMethodLabel, reversalPresentation } from "@/services/paymentPresentation.js";
import { resolvePatientDisplayName } from "@/utils/patientDisplayName.js";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";

function parseAmountMajor(row = {}) {
  const cents = Number(row?.amount_cents);
  if (Number.isFinite(cents) && cents !== 0) return cents / 100;

  const raw = Number(row?.amount);
  if (!Number.isFinite(raw)) return 0;
  return raw > 999 ? raw / 100 : raw;
}

function parseMetaMajorAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return numeric > 999 ? numeric / 100 : numeric;
}

function normalizePaymentRow(row = {}, { role = "patient" } = {}) {
  const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
  const sellerRole = String(row?.seller_role || meta?.seller_role || "").trim().toLowerCase();
  const paymentType = String(row?.type || meta?.payment_type || "").trim().toLowerCase();
  const amountOverride =
    role === "doctor" && (sellerRole === "doctor" || paymentType === "appointment")
      ? parseMetaMajorAmount(
          row?.seller_net_amount_cents ??
            meta?.doctor_net_amount_cents ??
            meta?.doctor_net_amount,
        )
      : null;
  const amount = amountOverride ?? parseAmountMajor(row);
  const rawStatus = String(row?.status || "").trim().toLowerCase() || "pending";
  const refundStatus = String(meta?.stripe_refund_status || "").trim().toLowerCase();
  const reversalReason = String(meta?.reversal_reason || "").trim().toLowerCase();
  const appointmentStateAfterReversal = String(meta?.appointment_state_after_reversal || "")
    .trim()
    .toLowerCase();
  const status =
    refundStatus === "pending"
      ? "refund_pending"
      : refundStatus === "succeeded"
        ? "refunded"
        : appointmentStateAfterReversal === "cancelled" && reversalReason
          ? rawStatus === "cancelled" || rawStatus === "refunded" || rawStatus === "refund_pending"
            ? rawStatus
            : "reversal_required"
          : rawStatus;
  const paidAt =
    /succeeded|paid|completed/i.test(status) ? row?.updated_at || row?.created_at || "" : "";

  return {
    id: row?.id || "",
    appointment_id: row?.appointment_id || "",
    prescription_id: row?.prescription_id || "",
    created_at: row?.created_at || "",
    updated_at: row?.updated_at || "",
    status,
    amount,
    currency: String(row?.currency || "CHF").toUpperCase(),
    paid_at: paidAt,
    invoice_number: row?.invoice_url || row?.stripe_payment_intent_id || "",
    reference:
      row?.stripe_session_id ||
      row?.stripe_payment_intent_id ||
      row?.prescription_id ||
      row?.appointment_id ||
      row?.id ||
      "",
    method: row?.method || "",
    payment_method_label: paymentMethodLabel(row),
    type: paymentType || "payment",
    seller_role: sellerRole || "",
    meta,
  };
}

function normalizeLegacyRow(row = {}) {
  const status = String(row?.status || "").trim() || "pending";
  const amount = Number(row?.amount || 0);
  const paidAt =
    /succeeded|paid|completed/i.test(status)
      ? row?.paid_at || row?.updated_at || row?.created_at || ""
      : "";

  return {
    id: row?.id || "",
    created_at: row?.created_at || "",
    updated_at: row?.updated_at || "",
    status,
    amount: Number.isFinite(amount) ? amount : 0,
    currency: String(row?.currency || "CHF").toUpperCase(),
    paid_at: paidAt,
    invoice_number: row?.stripe_invoice_id || row?.id || "",
    reference: row?.stripe_invoice_id || row?.id || "",
    method: row?.billing_reason || "stripe",
    meta: {
      billing_reason: row?.billing_reason || "",
      source: "legacy_billing_history",
    },
  };
}

function normalizeAppointmentFallbackRow(row = {}, { role = "patient" } = {}) {
  const grossAmount = Number(row?.booked_service_price || 0);
  const gross = Number.isFinite(grossAmount) ? grossAmount : 0;
  const amount =
    role === "doctor" ? Math.round(Math.max(gross * 0.9, 0) * 100) / 100 : gross;
  const status =
    row?.is_paid || /paid|succeeded|completed/i.test(String(row?.status || ""))
      ? "paid"
      : String(row?.status || "").trim() || "pending";
  const paidAt =
    /paid|succeeded|completed/i.test(status)
      ? row?.updated_at || row?.created_at || row?.appointment_date || ""
      : "";

  return {
    id: row?.payment_intent_id || row?.id || "",
    appointment_id: row?.id || "",
    created_at: row?.created_at || row?.appointment_date || "",
    updated_at: row?.updated_at || row?.created_at || "",
    status,
    amount,
    currency: String(row?.booked_service_currency || "CHF").toUpperCase(),
    paid_at: paidAt,
    invoice_number: row?.payment_intent_id || row?.id || "",
    reference: row?.payment_intent_id || row?.id || "",
    method: "appointment-fallback",
    meta: {
      source: "appointment_fallback",
      appointment_status: row?.status || "",
      is_paid: !!row?.is_paid,
    },
  };
}

function buildBillingDedupKey(row = {}) {
  const appointmentId = String(row?.appointment_id || "").trim();
  if (appointmentId) return `appointment:${appointmentId}`;

  const prescriptionId = String(row?.prescription_id || "").trim();
  if (prescriptionId) return `prescription:${prescriptionId}`;

  const paymentLinkId = String(row?.meta?.payment_link_id || "").trim();
  if (paymentLinkId) return `payment_link:${paymentLinkId}`;

  const paymentIntentId = String(
    row?.meta?.stripe_payment_intent_id || row?.invoice_number || "",
  ).trim();
  if (paymentIntentId) return `payment_intent:${paymentIntentId}`;

  const reference = String(row?.reference || "").trim();
  if (reference) return `reference:${reference}`;

  const rowId = String(row?.id || "").trim();
  return rowId ? `row:${rowId}` : "";
}

function isOpenStatus(status = "") {
  return [
    "pending",
    "processing",
    "requires_action",
    "requires_payment",
    "requires_payment_method",
  ].includes(String(status || "").trim().toLowerCase());
}

function shouldHidePreBookingAttempt(row = {}) {
  const status = String(row?.status || "").trim().toLowerCase();
  if (!isOpenStatus(status)) return false;

  const appointmentId = String(row?.appointment_id || "").trim();
  const prescriptionId = String(row?.prescription_id || "").trim();
  if (appointmentId || prescriptionId) return false;

  const type = String(row?.type || "").trim().toLowerCase();
  const method = String(row?.method || "").trim().toLowerCase();
  const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
  const paymentLinkId = String(meta?.payment_link_id || "").trim();

  if (type === "appointment" || type === "consultation") return true;
  if (method === "sponsor-payment-link" && !paymentLinkId) return true;
  if (method === "sponsor-payment-link" && paymentLinkId) return true;

  return false;
}

async function loadPatientPayments(userId) {
  const { data, error } = await supabase
    .from("payments")
    .select("*")
    .or(`user_id.eq.${userId},patient_user_id.eq.${userId}`)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (Array.isArray(data) ? data : []).map((row) =>
    normalizePaymentRow(row, { role: "patient" }),
  );
}

async function loadDoctorPayments(userId) {
  const cleanUserId = String(userId || "").trim();
  if (!cleanUserId) return [];

  const { data: doctorProfiles, error: doctorProfilesError } = await supabase
    .from("doctor_profiles")
    .select("id, user_id")
    .or(`user_id.eq.${cleanUserId},id.eq.${cleanUserId}`);

  if (doctorProfilesError) throw doctorProfilesError;

  const doctorRefs = Array.from(
    new Set(
      [cleanUserId]
        .concat(
          (Array.isArray(doctorProfiles) ? doctorProfiles : []).flatMap((row) => [
            String(row?.id || "").trim(),
            String(row?.user_id || "").trim(),
          ]),
        )
        .filter(Boolean),
    ),
  );

  const { data: appointments, error: appointmentsError } = await supabase
    .from("appointments")
    .select("id")
    .in("doctor_id", doctorRefs);

  if (appointmentsError) throw appointmentsError;

  const appointmentIds = (Array.isArray(appointments) ? appointments : [])
    .map((row) => String(row?.id || "").trim())
    .filter(Boolean);

  const [appointmentPaymentsRes, directDoctorPaymentsRes] = await Promise.all([
    appointmentIds.length
      ? supabase
          .from("payments")
          .select("*")
          .in("appointment_id", appointmentIds)
          .order("created_at", { ascending: false })
      : Promise.resolve({ data: [], error: null }),
    supabase
      .from("payments")
      .select("*")
      .eq("doctor_user_id", cleanUserId)
      .order("created_at", { ascending: false }),
  ]);

  if (appointmentPaymentsRes.error) throw appointmentPaymentsRes.error;
  if (directDoctorPaymentsRes.error) throw directDoctorPaymentsRes.error;

  const seen = new Set();
  return [...(appointmentPaymentsRes.data || []), ...(directDoctorPaymentsRes.data || [])]
    .filter((row) => {
      const id = String(row?.id || "").trim();
      if (!id) return true;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map((row) => normalizePaymentRow(row, { role: "doctor" }));
}

async function loadPatientPaidAppointmentsFallback(userId) {
  const cleanUserId = String(userId || "").trim();
  if (!cleanUserId) return [];

  let patientId = "";
  try {
    patientId = String(await getCurrentPatientId()).trim();
  } catch {
    patientId = "";
  }
  if (!patientId) return [];

  const { data, error } = await supabase
    .from("appointments")
    .select(
      "id,created_at,updated_at,appointment_date,status,is_paid,booked_service_price,booked_service_currency,payment_intent_id",
    )
    .eq("patient_id", patientId)
    .eq("is_paid", true)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (Array.isArray(data) ? data : []).map((row) =>
    normalizeAppointmentFallbackRow(row, { role: "patient" }),
  );
}

async function loadDoctorPaidAppointmentsFallback(userId) {
  const cleanUserId = String(userId || "").trim();
  if (!cleanUserId) return [];

  const { data: doctorProfiles, error: doctorProfilesError } = await supabase
    .from("doctor_profiles")
    .select("id, user_id")
    .or(`user_id.eq.${cleanUserId},id.eq.${cleanUserId}`);

  if (doctorProfilesError) throw doctorProfilesError;

  const doctorRefs = Array.from(
    new Set(
      [cleanUserId]
        .concat(
          (Array.isArray(doctorProfiles) ? doctorProfiles : []).flatMap((row) => [
            String(row?.id || "").trim(),
            String(row?.user_id || "").trim(),
          ]),
        )
        .filter(Boolean),
    ),
  );

  const { data, error } = await supabase
    .from("appointments")
    .select(
      "id,created_at,updated_at,appointment_date,status,is_paid,booked_service_price,booked_service_currency,payment_intent_id",
    )
    .in("doctor_id", doctorRefs)
    .eq("is_paid", true)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (Array.isArray(data) ? data : []).map((row) =>
    normalizeAppointmentFallbackRow(row, { role: "doctor" }),
  );
}

async function loadLegacyBillingHistory(userId) {
  const { data, error } = await supabase
    .from("subscription_billing_history")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (Array.isArray(data) ? data : []).map(normalizeLegacyRow);
}

async function hydrateAppointmentBillingContext(rows = []) {
  const appointmentIds = Array.from(new Set(rows.map((row) => String(row?.appointment_id || "").trim()).filter(Boolean)));
  if (!appointmentIds.length) return rows;
  try {
    const { data: appointments, error } = await supabase
      .from("appointments")
      .select("id,appointment_date,booked_service_name,doctor_id,patient_id,consultation_type")
      .in("id", appointmentIds);
    if (error || !Array.isArray(appointments)) return rows;
    const appointmentById = new Map(appointments.map((appointment) => [String(appointment.id), appointment]));
    const doctorUserIds = Array.from(new Set(appointments.map((appointment) => String(appointment?.doctor_id || "").trim()).filter(Boolean)));
    const { data: doctors } = doctorUserIds.length
      ? await supabase.from("doctor_profiles").select("user_id,full_name,first_name,last_name,salutation").in("user_id", doctorUserIds)
      : { data: [] };
    const doctorNameByUserId = new Map((Array.isArray(doctors) ? doctors : []).map((doctor) => [
      String(doctor?.user_id || ""), resolvePatientFacingDoctorName(doctor, "AfriMed clinician"),
    ]));
    const patientIds = Array.from(new Set(appointments.map((appointment) => String(appointment?.patient_id || "").trim()).filter(Boolean)));
    const { data: patientRefs } = patientIds.length
      ? await supabase.from("patients").select("id,user_id").in("id", patientIds)
      : { data: [] };
    const patientUserIdByPatientId = new Map((Array.isArray(patientRefs) ? patientRefs : []).map((patient) => [String(patient?.id || ""), String(patient?.user_id || "")]));
    const patientUserIds = Array.from(new Set([...patientUserIdByPatientId.values()].filter(Boolean)));
    const { data: patientProfiles } = patientUserIds.length
      ? await supabase.from("patient_profiles").select("user_id,full_name,first_name,last_name,salutation").in("user_id", patientUserIds)
      : { data: [] };
    const patientNameByUserId = new Map((Array.isArray(patientProfiles) ? patientProfiles : []).map((patient) => [
      String(patient?.user_id || ""), resolvePatientDisplayName({ profile: patient, fallback: "Patient" }),
    ]));
    const { data: reversals } = await supabase
      .from("payment_reversals")
      .select("id,payment_id,appointment_id,state,provider,provider_reversal_reference,provider_status,amount_minor,currency,requested_at,initiated_at,verified_at,metadata")
      .in("appointment_id", appointmentIds)
      .order("requested_at", { ascending: false });
    const reversalByPaymentId = new Map((Array.isArray(reversals) ? reversals : []).map((reversal) => [String(reversal?.payment_id || ""), reversal]));
    return rows.flatMap((row) => {
      const appointment = appointmentById.get(String(row?.appointment_id || ""));
      const reversal = reversalByPaymentId.get(String(row?.id || "")) || null;
      const reversalState = reversalPresentation(reversal || {});
      if (!appointment) return [row];
      const paidEvent = {
        ...row,
        appointment,
        clinician_name: doctorNameByUserId.get(String(appointment?.doctor_id || "")) || "AfriMed clinician",
        patient_name: patientNameByUserId.get(patientUserIdByPatientId.get(String(appointment?.patient_id || ""))) || "Patient",
        payment_method_label: paymentMethodLabel(row),
        reversal,
        reversal_presentation: reversalState,
        // The original payment remains an immutable paid financial event.
        status: row.status,
        document_kind: "appointment",
      };
      if (!reversal?.id) return [paidEvent];
      return [
        paidEvent,
        {
          ...paidEvent,
          id: `refund:${reversal.id}`,
          reference: reversal.provider_reversal_reference || reversal.id,
          created_at: reversal.verified_at || reversal.initiated_at || reversal.requested_at || row.created_at,
          updated_at: reversal.verified_at || reversal.initiated_at || reversal.requested_at || row.updated_at,
          paid_at: reversal.verified_at || reversal.initiated_at || reversal.requested_at || "",
          status: reversalState.state === "refunded" ? "refunded" : "refund_pending",
          type: "refund",
          document_kind: "refund",
        },
      ];
    });
  } catch {
    return rows;
  }
}

export async function listRecoverableSponsorPaymentLinks(role = "patient", userId = null) {
  const cleanUserId = String(userId || "").trim();
  if (!cleanUserId) return [];

  let query = supabase
    .from("payments")
    .select("id, created_at, status, method, meta");

  if (role === "doctor") {
    query = query
      .eq("method", "sponsor-payment-link")
      .contains("meta", { doctor_user_id: cleanUserId })
      .order("created_at", { ascending: false })
      .limit(12);
  } else {
    query = query
      .eq("user_id", cleanUserId)
      .eq("method", "sponsor-payment-link")
      .order("created_at", { ascending: false })
      .limit(12);
  }

  const { data, error } = await query;
  if (error) throw error;

  const seen = new Set();
  return (Array.isArray(data) ? data : [])
    .filter((row) => {
      const status = String(row?.status || "").trim().toLowerCase();
      return !/paid|succeeded|completed/i.test(status);
    })
    .map((row) => {
      const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
      return {
        id: String(row?.id || "").trim(),
        created_at: String(row?.created_at || "").trim(),
        paymentLinkId: String(meta?.payment_link_id || "").trim(),
      };
    })
    .filter((row) => row.paymentLinkId)
    .filter((row) => {
      if (seen.has(row.paymentLinkId)) return false;
      seen.add(row.paymentLinkId);
      return true;
    });
}

export async function getBillingHistory(role = "patient", userId = null) {
  if (!String(userId || "").trim()) {
    return { data: [], error: null, source: "empty" };
  }

  const [paymentsResult, legacyResult] = await Promise.allSettled([
    role === "doctor" ? loadDoctorPayments(userId) : loadPatientPayments(userId),
    loadLegacyBillingHistory(userId),
  ]);
  const appointmentFallbackResult = await Promise.allSettled([
    role === "doctor"
      ? loadDoctorPaidAppointmentsFallback(userId)
      : loadPatientPaidAppointmentsFallback(userId),
  ]);

  const payments =
    paymentsResult.status === "fulfilled" && Array.isArray(paymentsResult.value)
      ? paymentsResult.value
      : [];
  const legacy =
    legacyResult.status === "fulfilled" && Array.isArray(legacyResult.value)
      ? legacyResult.value
      : [];
  const appointmentFallback =
    appointmentFallbackResult[0]?.status === "fulfilled" &&
    Array.isArray(appointmentFallbackResult[0].value)
      ? appointmentFallbackResult[0].value
      : [];

  const seen = new Set();
  const combined = [...payments, ...legacy, ...appointmentFallback]
    .filter(Boolean)
    .filter((row) => !shouldHidePreBookingAttempt(row))
    .filter((row) => {
      const key = buildBillingDedupKey(row);
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => {
      const left = new Date(b?.created_at || 0).getTime();
      const right = new Date(a?.created_at || 0).getTime();
      return left - right;
    });

  if (combined.length) {
    const hydrated = await hydrateAppointmentBillingContext(combined);
    // Financial history is event history: a refund has its own later timestamp.
    hydrated.sort((a, b) => {
      const left = new Date(b?.paid_at || b?.updated_at || b?.created_at || 0).getTime();
      const right = new Date(a?.paid_at || a?.updated_at || a?.created_at || 0).getTime();
      return left - right;
    });
    return { data: hydrated, error: null, source: "combined" };
  }

  const paymentsError =
    paymentsResult.status === "rejected" ? paymentsResult.reason : null;
  const legacyError =
    legacyResult.status === "rejected" ? legacyResult.reason : null;

  return {
    data: [],
    error: legacyError?.message ? legacyError : paymentsError,
    source: paymentsError || legacyError ? "error" : "empty",
  };
}

export default {
  getBillingHistory,
  listRecoverableSponsorPaymentLinks,
};
